/*
 **	OMNI_IQ Dialog Context (c) 2025 Silver Falls Capital. All Rights Reserved
 **
 **	This code is unlicensed and may not be copied, used, modified,
 **	compiled, run, or distributed in any form without the explicit
 **	written permission of Silver Falls Capital.
 **
 **	Author(s): Oliver Sturrock olst@silverfallscapital.com
 **
 **/

import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useContext, useState } from 'react';
import { Modal } from '../components/common/Modal';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { theme } from '../theme';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'primary';
}

interface PromptOptions {
  title?: string;
  message: string;
  defaultValue?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
}

interface DialogContextValue {
  confirm: (options: ConfirmOptions | string) => Promise<boolean>;
  prompt: (options: PromptOptions | string) => Promise<string | null>;
}

const DialogContext = createContext<DialogContextValue | undefined>(undefined);

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};

interface DialogState {
  isOpen: boolean;
  type: 'confirm' | 'prompt' | null;
  options: ConfirmOptions | PromptOptions | null;
  resolve: ((value: any) => void) | null;
}

export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    type: null,
    options: null,
    resolve: null,
  });

  const [promptValue, setPromptValue] = useState('');

  const confirm = (options: ConfirmOptions | string): Promise<boolean> => {
    return new Promise((resolve) => {
      const opts: ConfirmOptions = typeof options === 'string'
        ? { message: options }
        : options;

      setDialogState({
        isOpen: true,
        type: 'confirm',
        options: opts,
        resolve,
      });
    });
  };

  const prompt = (options: PromptOptions | string): Promise<string | null> => {
    return new Promise((resolve) => {
      const opts: PromptOptions = typeof options === 'string'
        ? { message: options }
        : options;

      setPromptValue(opts.defaultValue || '');
      setDialogState({
        isOpen: true,
        type: 'prompt',
        options: opts,
        resolve,
      });
    });
  };

  const handleClose = () => {
    if (dialogState.resolve) {
      if (dialogState.type === 'confirm') {
        dialogState.resolve(false);
      } else if (dialogState.type === 'prompt') {
        dialogState.resolve(null);
      }
    }
    setDialogState({ isOpen: false, type: null, options: null, resolve: null });
    setPromptValue('');
  };

  const handleConfirm = () => {
    if (dialogState.resolve) {
      if (dialogState.type === 'confirm') {
        dialogState.resolve(true);
      } else if (dialogState.type === 'prompt') {
        dialogState.resolve(promptValue);
      }
    }
    setDialogState({ isOpen: false, type: null, options: null, resolve: null });
    setPromptValue('');
  };

  const renderConfirmDialog = (options: ConfirmOptions) => (
    <Modal
      isOpen={dialogState.isOpen}
      onClose={handleClose}
      title={options.title || 'Confirm'}
      size="sm"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
        <p style={theme.typography.body}>{options.message}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm, marginTop: theme.spacing.md }}>
          <Button
            variant="ghost"
            onClick={handleClose}
          >
            {options.cancelText || 'Cancel'}
          </Button>
          <Button
            variant={options.variant || 'primary'}
            onClick={handleConfirm}
          >
            {options.confirmText || 'Confirm'}
          </Button>
        </div>
      </div>
    </Modal>
  );

  const renderPromptDialog = (options: PromptOptions) => (
    <Modal
      isOpen={dialogState.isOpen}
      onClose={handleClose}
      title={options.title || 'Input Required'}
      size="sm"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
        <p style={theme.typography.body}>{options.message}</p>
        <Input
          value={promptValue}
          onChange={(e) => setPromptValue(e.target.value)}
          placeholder={options.placeholder || ''}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && promptValue.trim()) {
              handleConfirm();
            }
          }}
          autoFocus
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm, marginTop: theme.spacing.md }}>
          <Button
            variant="ghost"
            onClick={handleClose}
          >
            {options.cancelText || 'Cancel'}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!promptValue.trim()}
          >
            {options.confirmText || 'OK'}
          </Button>
        </div>
      </div>
    </Modal>
  );

  return (
    <DialogContext.Provider value={{ confirm, prompt }}>
      {children}
      {dialogState.type === 'confirm' && dialogState.options && renderConfirmDialog(dialogState.options as ConfirmOptions)}
      {dialogState.type === 'prompt' && dialogState.options && renderPromptDialog(dialogState.options as PromptOptions)}
    </DialogContext.Provider>
  );
};
